import { useEffect, useMemo, useState } from 'react'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'react-router'

import { useFetchList, useReload } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import useSectionTitle from 'hooks/useSectionTitle'

import RulesEditor from './RulesEditor'
import useRulesMetadata from './useRulesMetadata'

import styles from './RulesEditorController.module.sass'

import {
	fields,
	output,
	getRulesSchema,
	numericOperators,
	listOperators,
	TaskTypes,
	RuleTypes,
	coordinators,
} from './RuleSchema'
import Rule from 'models/Rule'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const FORM_ID = 'EDITOR_FORM_ID'

const availableFields = (i18n) =>
	fields.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableNumericOperators = (i18n) =>
	numericOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableListOperators = (i18n) =>
	listOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableCoordinators = (i18n) =>
	coordinators.map((value) => ({
		value,
		text: i18n.translate(`${value}Coordinator`),
	}))

const availableOutput = (i18n) =>
	output.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const RulesEditorController = () => {
	const { i18n, expireSession, pushNotification } = useGlobal()
	const title = useSectionTitle(i18n, 'taskType')
	const { ruleType, taskType } = useParams()

	const apiEndpoint = `/rules?ruleType=${RuleTypes[ruleType]}&taskType=${TaskTypes[taskType]}`
	const [{ endpoint, initialData }, setInitialData] = useState({
		endpoint: '',
		initialData: [],
	})
	const [submitError, setSubmitError] = useState(null)

	const metadata = useRulesMetadata()
	const reload = useReload()
	const { contents, state } = useFetchList([apiEndpoint], [Rule.fromJSON])

	const rulesRaw = useMemo(
		() =>
			state === 'hasValue' && Array.isArray(contents[0])
				? contents[0]
				: [],
		[contents, state]
	)

	const defaultValues = {
		rules: Array.isArray(contents[0])
			? contents[0].sort((a, b) => a.order - b.order)
			: [],
	}

	const fieldsOptions = useMemo(() => availableFields(i18n), [i18n])
	const numericOperatorsOptions = useMemo(
		() => availableNumericOperators(i18n),
		[i18n]
	)
	const listOperatorsOptions = useMemo(
		() => availableListOperators(i18n),
		[i18n]
	)
	const coordinatorOptions = useMemo(
		() => availableCoordinators(i18n),
		[i18n]
	)
	const outputOptions = useMemo(() => availableOutput(i18n), [i18n])
	const options = {
		...metadata,
		fields: fieldsOptions,
		numericOperators: numericOperatorsOptions,
		listOperators: listOperatorsOptions,
		outputOptions,
		coordinatorOptions,
	}

	const methods = useForm({
		resolver: zodResolver(getRulesSchema(options, i18n)),
		shouldUseCustomValidity: true,
		defaultValues,
	})

	const { reset, formState, handleSubmit } = methods

	useEffect(
		() => () => {
			setInitialData({ endpoint: '', initialData: [] })
		},
		[]
	)

	useEffect(() => {
		if (
			endpoint !== apiEndpoint &&
			state === 'hasValue' &&
			Array.isArray(contents[0])
		) {
			setInitialData({
				endpoint: apiEndpoint,
				initialData: contents[0],
			})
		}
	}, [contents.length, endpoint, rulesRaw, state, contents, apiEndpoint])

	useEffect(() => {
		reset({ rules: initialData })
	}, [initialData, reset])

	const onSubmit = (formData) => {
		const rulesForSubmission = formData.rules.map((rule) =>
			Rule.toJSON(rule)
		)
		setSubmitError(null)
		const endpoint = `${API_URL}/api/rules`

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: {
					ruleType: RuleTypes[ruleType],
					taskType: TaskTypes[taskType],
					rules: rulesForSubmission,
				},
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				setSubmitError(null)
				reload([apiEndpoint])
				pushNotification(
					undefined,
					'success',
					i18n`Rule created successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				} else {
					const errorMessage = translateErrorMessage(
						e?.formErrors?.[0] ?? e?.fieldErrors?.[0] ?? e,
						i18n
					)

					setSubmitError(errorMessage)
				}
			})
	}

	return (
		<>
			<span className={styles['background']} />
			<div
				className={`${styles['actions-container']} ${
					formState.isSubmitting ? styles['--is-submitting'] : ''
				} ${submitError ? styles['--has-error'] : ''}`}
			>
				{formState.isSubmitting && (
					<span className={styles['progress-background']} />
				)}
				<HStack justify='space-between'>
					<span>
						<span className={styles['title']}>
							<h4>{useSectionTitle(i18n, 'ruleType')} -</h4>
							<h5>{title}</h5>
						</span>
						<small>
							{formState.isSubmitting
								? i18n`Submitting`
								: submitError}
						</small>
					</span>
					<HStack gap='small'>
						<ActionButton
							type='button'
							onClick={() => {
								const isDirty =
									Object.entries(formState.dirtyFields)
										.length > 0
								if (isDirty) {
									reset(defaultValues)
								}
							}}
						>
							{i18n`Reset`}
						</ActionButton>
						<ActionButton
							primary
							type='submit'
							form={FORM_ID}
							disabled={formState.isSubmitting}
						>
							{formState.isSubmitting
								? i18n`Submitting`
								: i18n`Save`}
						</ActionButton>
					</HStack>
				</HStack>
			</div>
			<div className={styles['editor']}>
				<FormProvider {...methods}>
					<RulesEditor
						id={FORM_ID}
						scrollableClassName={styles['editor']}
						ruleType={RuleTypes[ruleType]}
						taskType={TaskTypes[taskType]}
						options={options}
						onSubmit={handleSubmit(onSubmit)}
					/>
				</FormProvider>
			</div>
		</>
	)
}

export default RulesEditorController
